import type { MenuProps } from 'antd';

export const LS_PREFIX = 'FR_';
export const LS_TOKEN_NAME = `${LS_PREFIX}TOKEN`;
/** The admin's own token, set aside while they are logged in as another user (see utils/impersonation). */
export const LS_IMPERSONATOR_TOKEN = `${LS_PREFIX}IMPERSONATOR_TOKEN`;
/** Where to send the admin back to when the Log In As session ends. */
export const LS_IMPERSONATOR_RETURN = `${LS_PREFIX}IMPERSONATOR_RETURN`;
/** Set on a Log In As switch: re-register this browser's push token for whoever is signed in after the reload. */
export const LS_PUSH_REBIND = `${LS_PREFIX}PUSH_REBIND`;
/**
 * Tenant role hierarchy as role => parent (by role name). Decides who an admin
 * may "Log In As": someone whose every role is at or below one of theirs.
 * Mirrors TenantUserRole::HIERARCHY_PARENTS — the backend is the authority.
 */
export const TENANT_ROLE_PARENTS: Record<string, string | null> = {
  'Super Admin': null,
  Admin: 'Super Admin',
  Manager: 'Admin',
  Supervisor: 'Manager',
  'CSR Manager': 'Supervisor',
  CSR: 'CSR Manager',
  'Sales Representative': 'Supervisor',
};
export const PHP_SESSION_NAME = `PHPSESSID`;
/** Window event fired when the backend answers CLOCK_IN_REQUIRED (see models/clockGate). */
export const CLOCK_IN_REQUIRED_EVENT = 'clockin-required';
export const DEFAULT_PER_PAGE_PAGINATION = 20;
export const AC_PER_PAGE_PAGINATION = 100;
export const EURO = '€';

export const DT_MAX_DATE = '2099-12-31';
export const DT_MIN_DATE = '1900-01-01';

export const DT_FORMAT_MY = 'MM.YYYY';
export const DT_FORMAT_DMY = 'DD.MM.YYYY';
export const DT_FORMAT_MDY = 'MM/DD/YYYY';
export const DT_FORMAT_DM = 'DD.MM.';
export const DT_FORMAT_DMY_HHMMSS = 'DD.MM.YYYY';
export const DT_FORMAT_YMD = 'YYYY-MM-DD';
export const DT_FORMAT_TIME_MAX = '23:59:59';
export const DT_FORMAT_TIME_MAX_S = ' 23:59:59';
export const DT_FORMAT_LIST = [DT_FORMAT_DMY, DT_FORMAT_YMD];

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const CURRENT_YEAR = new Date().getFullYear();



export const PageSizeOptionsOnCard3 = ['12', '24', '48', '60', '120'];

export const DEFAULT_PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

export const THIS_YEAR = new Date().getFullYear();

// Dispatch Colors
export const DispatchColors: {
    [key: string]: {
        [key: string]: string;
    };
} = {
    job_status: {
        "10": "#10B981",  // Completed - Emerald
        "survey": "#A855F7",  // Survey - Purple
        "sold": "#D97706",  // Sold (internal-only) - Dark Amber
        "20": "#3B82F6",  // In Progress - Blue
        "40": "#EF4444",  // Canceled - Red
        "140": "#F59E0B", // Incompleted - Amber
        "150": "#8B5CF6", // On Hold - Violet
        "250": "#6B7280", // Assigned - Gray
        "260": "#06B6D4"  // Accepted - Cyan
    },
    appointment_status: {
        "30": "#22C55E", // Initial Set
        "70": "#1D4ED8", // On My Way
        "80": "#3B82F6", // Technician May Be Delayed to Appointment
        "90": "#16A34A", // Arrived
        "120": "#F59E0B", // Customer Missed Appointment
        "200": "#22C55E", // Customer Appointment Set
        "270": "#22C55E", // Reschedule Appointment Set
        "400": "#22C55E", // Return Appointment Set
        "430": "#22C55E", // Installation Appointment Set
        "220": "#7C3AED", // Appointment Complete
        "230": "#DC2626", // Appointment Cancelled
        "280": "#F59E0B", // Left Customer a Message to Schedule - Amber
        "60": "#EF4444", // Unable to Contact Customer to Schedule - Red
    },
    parking_status: {
        "parked": "#F59E0B",    // Parked - Amber
        "driving": "#3B82F6",   // Driving - Blue
        "unparked": "#10B981"   // Unparked - Emerald
    },
    parts_status: {
        "100": "#059669",
        "160": "#059669",
        "380": "#059669", // Parts Ordered
        "390": "#3B82F6", // Equipment Ordered - Blue
        "410": "#10B981", // Parts Arrived - Emerald
        "420": "#10B981"  // Equipment Arrived - Emerald
    },
    authorization_status: {
        "290": "#16A34A",
        "350": "#2563EB",
        "360": "#D97706",
        "370": "#9333EA",
        "450": "#DC2626",
        "470": "#059669",
        "480": "#059669"
    },
    cil_status: {
        "320": "#059669",
        "330": "#059669",
        "340": "#059669"
    },
    invoice_status: {
        "440": "#059669"
    },
    cash_status: {
        "500": "#059669"
    },
    appliance_status: {
        "300": "#059669",
        "310": "#059669"
    },
    ncc_status: {
        "510": "#059669"
    },
    quote_status: {
        "530": "#059669",
        "540": "#059669",
        "560": "#059669"
    },
    drive_status: {
        "70": "#1D4ED8", // Technician in Route - Blue
        "90": "#16A34A"  // Technician Arrived - Green
    },
    complete: {
        "complete": "#10B981"  // Complete - Emerald (same as job_status completed)
    }
};

// Dispatch ContextMenu items
export const DispatchContextMenuItems: MenuProps['items'] = [
    {
        label: 'Initial Appointment Scheduled',
        key: 'appointment_status:30',
    },
    {
        label: 'Customer Appointment Set',
        key: 'appointment_status:200',
    },
    {
        label: 'Set Appointment',
        key: 'set_appointment_submenu',
        children: [
            {
                label: 'Reschedule Appointment Set',
                key: 'appointment_status:270',
            },
            {
                label: 'Return Appointment Set',
                key: 'appointment_status:400',
            },
            {
                label: 'Installation Appointment Set',
                key: 'appointment_status:430',
            },
        ],
    },
    {
        label: 'Left Customer a Message to Schedule',
        key: 'appointment_status:280',
    },
    {
        label: 'Unable to Contact Customer to Schedule',
        key: 'appointment_status:60',
    },
    {
        label: 'On My Way',
        key: 'appointment_status:70',
    },
    {
        label: 'Missed appointment',
        key: 'appointment_status:120',
    },
    {
        label: 'Arrived',
        key: 'appointment_status:90',
    },
    {
        label: 'Parts Ordered',
        key: 'parts_status:380',
    },
    {
        label: 'Equipment Ordered',
        key: 'parts_status:390',
    },
    {
        label: 'Parts Have Arrived',
        key: 'parts_status:410',
    },
    {
        label: 'Equipment Arrived',
        key: 'parts_status:420',
    },
    {
        label: 'Survey',
        key: 'job_status:survey',
    },
    {
        // Internal-only: no Frontdoor sync, no SMS triggers, hidden from techs.
        label: 'Sold',
        key: 'job_status:sold',
    },
    {
        label: 'Incomplete',
        key: 'job_status:140',
    },
    {
        label: 'Hold',
        key: 'job_status:150',
    },
    {
        label: 'Cancel',
        key: 'job_status:40',
    },
    {
        label: 'Complete',
        key: 'job_status:10',
    },
];

// Flat options for the details panel <Select> — uses optgroup for Set Appointment sub-types.
// Kept separate from DispatchContextMenuItems because the Select cannot render nested children.
export const DispatchStatusSelectOptions = [
    { label: 'Initial Appointment Scheduled', value: 'appointment_status:30' },
    { label: 'Customer Appointment Set',      value: 'appointment_status:200' },
    {
        label: 'Set Appointment',
        options: [
            { label: 'Reschedule Appointment Set',    value: 'appointment_status:270' },
            { label: 'Return Appointment Set',         value: 'appointment_status:400' },
            { label: 'Installation Appointment Set',   value: 'appointment_status:430' },
        ],
    },
    { label: 'Left Customer a Message to Schedule',    value: 'appointment_status:280' },
    { label: 'Unable to Contact Customer to Schedule', value: 'appointment_status:60' },
    { label: 'On My Way',       value: 'appointment_status:70' },
    { label: 'Missed Appointment', value: 'appointment_status:120' },
    { label: 'Arrived',         value: 'appointment_status:90' },
    { label: 'Parts Ordered',   value: 'parts_status:380' },
    { label: 'Equipment Ordered', value: 'parts_status:390' },
    { label: 'Parts Have Arrived', value: 'parts_status:410' },
    { label: 'Equipment Arrived',  value: 'parts_status:420' },
    { label: 'Incomplete', value: 'job_status:140' },
    { label: 'Hold',       value: 'job_status:150' },
    { label: 'Cancel',     value: 'job_status:40' },
    { label: 'Complete',   value: 'job_status:10' },
    { label: 'Survey',     value: 'job_status:survey' },
    { label: 'Sold',       value: 'job_status:sold' }, // internal-only
];

export enum InvoiceStatus {
    DRAFT = 'draft',
    SENT = 'sent',
    PENDING = 'pending',
    OVERDUE = 'overdue',
    PARTIALLY_PAID = 'partially_paid',
    PAID = 'paid',
    REFUNDED = 'refunded',
    CANCELED = 'canceled',
    APPROVED = 'approved',
    COMPLETED = 'completed',
    // SignNow statuses
    SIGN_PENDING = 'sign_pending',
    SIGN_CANCELED = 'sign_canceled',
    SIGN_INVITED = 'sign_invited',
    SIGNED = 'signed',
}

export const InvoiceStatusOptions = [
    { value: InvoiceStatus.DRAFT, label: 'Draft' },
    { value: InvoiceStatus.SENT, label: 'Sent' },
    { value: InvoiceStatus.PENDING, label: 'Pending' },
    { value: InvoiceStatus.OVERDUE, label: 'Overdue' },
    { value: InvoiceStatus.PARTIALLY_PAID, label: 'Partially Paid' },
    { value: InvoiceStatus.PAID, label: 'Paid' },
    { value: InvoiceStatus.REFUNDED, label: 'Refunded' },
    { value: InvoiceStatus.CANCELED, label: 'Canceled' },
    { value: InvoiceStatus.APPROVED, label: 'Approved' },
    { value: InvoiceStatus.COMPLETED, label: 'Completed' },
    // SignNow statuses
    { value: InvoiceStatus.SIGN_PENDING, label: 'Sign Pending' },
    { value: InvoiceStatus.SIGN_CANCELED, label: 'Sign Canceled' },
    { value: InvoiceStatus.SIGN_INVITED, label: 'Sign Invited' },
    { value: InvoiceStatus.SIGNED, label: 'Signed' },
];

/** SignNow e-signature can only be started at or above this invoice total. Mirrors DispatchInvoice::SIGNNOW_MIN_TOTAL. */
export const SIGNNOW_MIN_TOTAL = 2500;

/** Statuses in which no (further) payment can be recorded. Mirrors DispatchInvoice::STATUSES_NO_PAYMENT. */
export const InvoiceStatusesNoPayment: string[] = [
    InvoiceStatus.PAID,
    InvoiceStatus.COMPLETED,
    InvoiceStatus.REFUNDED,
    InvoiceStatus.CANCELED,
    InvoiceStatus.SIGN_CANCELED,
];

export const InvoicePaymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'card', label: 'Card' },
    { value: 'ach', label: 'ACH / Bank Transfer' },
    { value: 'financing', label: 'Financing' },
    { value: 'other', label: 'Other' },
];

export const InvoicePaymentMethodKv: Record<string, string> = InvoicePaymentMethodOptions.reduce((prev: any, x) => {
    prev[x.value] = x.label;
    return prev;
}, {});

/** dispatch_invoice_payment.status. Mirrors DispatchInvoicePayment::STATUS_*. */
export const InvoicePaymentStatusOptions = [
    { value: 'pending', label: 'Pending', color: 'processing' },
    { value: 'authorized', label: 'Authorized', color: 'processing' },
    { value: 'succeeded', label: 'Succeeded', color: 'success' },
    { value: 'failed', label: 'Failed', color: 'error' },
    { value: 'voided', label: 'Voided', color: 'default' },
    { value: 'partially_refunded', label: 'Partially Refunded', color: 'warning' },
    { value: 'refunded', label: 'Refunded', color: 'warning' },
];

export const InvoicePaymentStatusKv: Record<string, { label: string; color: string }> = InvoicePaymentStatusOptions.reduce((prev: any, x) => {
    prev[x.value] = x;
    return prev;
}, {});

/** payment_gateways.provider. Mirrors PaymentGateway::PROVIDER_LABELS. */
export const PaymentGatewayProviderKv: Record<string, string> = {
    stripe: 'Stripe',
    square: 'Square',
    authorize_net: 'Authorize.net',
    paypal: 'PayPal',
    wisetack: 'Wisetack',
    other: 'Other',
};

export const InvoiceStatusKv = InvoiceStatusOptions.reduce((prev: any, x: any) => {
    prev[x.value] = x.label;
    return prev;
}, {});


export enum EstimateStatus {
    DRAFT = 'draft',
    SENT = 'sent',
    APPROVED = 'approved',
    EXPIRED = 'expired',
    DECLINED = 'declined',
    CLOSED = 'closed',
}

export const EstimateStatusOptions = [
    { value: EstimateStatus.DRAFT, label: 'Draft' },
    { value: EstimateStatus.SENT, label: 'Sent' },
    { value: EstimateStatus.APPROVED, label: 'Approved' },
    { value: EstimateStatus.EXPIRED, label: 'Expired' },
    { value: EstimateStatus.CLOSED, label: 'Closed' },
    { value: EstimateStatus.DECLINED, label: 'Declined' },
];

export const EstimateStatusKv = EstimateStatusOptions.reduce((prev: any, x: any) => {
    prev[x.value] = x.label;
    return prev;
}, {});

export enum EstimateProposalStatus {
    DRAFT = 'draft',
    PRESENTED = 'presented',
    ACCEPTED = 'accepted',
    EXPIRED = 'expired',
    CANCELLED = 'cancelled',
}

export const EstimateProposalStatusOptions = [
    { value: EstimateProposalStatus.DRAFT, label: 'Draft' },
    { value: EstimateProposalStatus.PRESENTED, label: 'Presented' },
    { value: EstimateProposalStatus.ACCEPTED, label: 'Accepted' },
    { value: EstimateProposalStatus.EXPIRED, label: 'Expired' },
    { value: EstimateProposalStatus.CANCELLED, label: 'Cancelled' },
];

export const EstimateProposalStatusKv = EstimateProposalStatusOptions.reduce((prev: any, x: any) => {
    prev[x.value] = x.label;
    return prev;
}, {});

export enum EstimateProposalTier {
    GOOD = 'good',
    BETTER = 'better',
    BEST = 'best',
}

export const EstimateProposalTierOptions = [
    { value: EstimateProposalTier.GOOD, label: 'Good' },
    { value: EstimateProposalTier.BETTER, label: 'Better' },
    { value: EstimateProposalTier.BEST, label: 'Best' },
];

export const EstimateProposalTierKv = EstimateProposalTierOptions.reduce((prev: any, x: any) => {
    prev[x.value] = x.label;
    return prev;
}, {});

/** Pre-qualification application opened from a proposal option's financing line. */
export const ESTIMATE_PROPOSAL_FINANCING_APPLY_URL = 'https://www.goodleap.dev/fastresponseheatingandcooling/e77eb578-b6ee-4a21-b4d8-ca36900dd49f';

/** Website page listing every financing plan we offer. */
export const ESTIMATE_PROPOSAL_FINANCING_OPTIONS_URL = 'https://fastresponseservice.com/financing-2/#financing-options';

/** Required disclosure wherever a monthly payment is shown to the customer. */
export const ESTIMATE_PROPOSAL_MONTHLY_PAYMENT_DISCLAIMER =
  'Monthly payments are estimates at the APR and term shown, subject to credit approval. Your actual APR, term and payment are set by the lender at approval and may differ. Total shown is the cash price. See financing agreement for complete terms.';


export enum DispatchStatus {
    ASSIGNED = 'assigned',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
    ARCHIVED = 'archived',
    PROGRESS = 'progress',
    HOLD = 'hold',
    COMPLETED = 'completed',
    CANCELED = 'cancelled',
    INCOMPLETED = 'incompleted',
    SURVEY = 'survey',
    SOLD = 'sold',
    // Appointment statuses
    LEFT_MESSAGE = '280',
    UNABLE_TO_CONTACT = '60',
    APPT_INITIAL_SET = 'appt_initial_set',
    APPT_SET = 'appt_set',
    APPT_ON_MY_WAY = 'appt_on_my_way',
    APPT_ARRIVED = 'appt_arrived',
    APPT_MISSED = 'appt_missed',
    APPT_RESCHEDULE_SET = 'appt_reschedule_set',
    APPT_RETURN_SET = 'appt_return_set',
    APPT_INSTALLATION_SET = 'appt_installation_set',
    // Parts / equipment statuses
    PARTS_ORDERED = 'parts_ordered',
    EQUIPMENT_ORDERED = 'equipment_ordered',
    PARTS_ARRIVED = 'parts_arrived',
    EQUIPMENT_ARRIVED = 'equipment_arrived',
}

// Order below mirrors DispatchStatusSelectOptions (the BasicInfo tab's status
// Select) for every status the two lists share, so the list filter dropdown
// and the details-panel status setter present statuses in the same sequence.
// ASSIGNED/ACCEPTED/ARCHIVED have no BasicInfo equivalent (derived states, not
// directly settable there), so they're kept up front as filter-only entries.
export const DispatchStatusOptions = [
    { value: DispatchStatus.ASSIGNED, label: 'Assigned' },
    { value: DispatchStatus.ACCEPTED, label: 'Accepted' },
    { value: DispatchStatus.ARCHIVED, label: 'Archived' },
    // Appointment statuses
    { value: DispatchStatus.APPT_INITIAL_SET, label: 'Initial Appointment Scheduled' },
    { value: DispatchStatus.APPT_SET, label: 'Customer Appointment Set' },
    { value: DispatchStatus.APPT_RESCHEDULE_SET, label: 'Reschedule Appointment Set' },
    { value: DispatchStatus.APPT_RETURN_SET, label: 'Return Appointment Set' },
    { value: DispatchStatus.APPT_INSTALLATION_SET, label: 'Installation Appointment Set' },
    { value: DispatchStatus.LEFT_MESSAGE, label: 'Left Customer a Message to Schedule' },
    { value: DispatchStatus.UNABLE_TO_CONTACT, label: 'Unable to Contact Customer to Schedule' },
    { value: DispatchStatus.APPT_ON_MY_WAY, label: 'On My Way' },
    { value: DispatchStatus.APPT_MISSED, label: 'Missed Appointment' },
    { value: DispatchStatus.APPT_ARRIVED, label: 'Arrived' },
    // Parts / equipment statuses
    { value: DispatchStatus.PARTS_ORDERED, label: 'Parts Ordered' },
    { value: DispatchStatus.EQUIPMENT_ORDERED, label: 'Equipment Ordered' },
    { value: DispatchStatus.PARTS_ARRIVED, label: 'Parts Have Arrived' },
    { value: DispatchStatus.EQUIPMENT_ARRIVED, label: 'Equipment Arrived' },
    { value: DispatchStatus.INCOMPLETED, label: 'Incompleted' },
    { value: DispatchStatus.HOLD, label: 'Hold' },
    { value: DispatchStatus.CANCELED, label: 'Canceled' },
    { value: DispatchStatus.COMPLETED, label: 'Completed' },
    { value: DispatchStatus.SURVEY, label: 'Survey' },
    { value: DispatchStatus.SOLD, label: 'Sold' },
];

export const DispatchStatusKv: Record<string, string> = {
    ...DispatchStatusOptions.reduce((prev: any, x: any) => {
        prev[x.value] = x.label;
        return prev;
    }, {}),
    // Display-only entries (not shown in filter dropdown)
    [DispatchStatus.PROGRESS]: 'In Progress',
};

// Job Status to DispatchStatus mapping
export const JobStatusToDispatchStatus: Record<string, DispatchStatus> = {
    '10': DispatchStatus.COMPLETED,
    'survey': DispatchStatus.SURVEY,
    'sold': DispatchStatus.SOLD,
    '20': DispatchStatus.PROGRESS,
    '40': DispatchStatus.CANCELED,
    '140': DispatchStatus.INCOMPLETED,
    '150': DispatchStatus.HOLD,
    '250': DispatchStatus.ASSIGNED,
    '260': DispatchStatus.ACCEPTED,
};

// Job Status descriptions
export const JobStatusDescriptions: Record<string, string> = {
    '10': 'Complete',
    'survey': 'Survey',
    'sold': 'Sold',
    '20': 'In Progress',
    '40': 'Cancel',
    '140': 'Incomplete',
    '150': 'On Hold',
    '250': 'Assigned',
    '260': 'Accepted',
};

/**
 * Get DispatchStatus from job_status code with additional business logic
 */
export function getDispatchStatusFromJobStatus(
    jobStatus: string | undefined,
    flowStep: string | undefined,
    dispatchUser: any,
    appointmentStatus?: string | undefined
): DispatchStatus | string {
    // Archived takes priority over everything else
    if (flowStep === 'archived') {
        return DispatchStatus.ARCHIVED;
    }

    // Terminal/definitive job statuses always take priority over flow_step.
    // The observer sets flow_step='appointment' when job_status changes to 40 or 140,
    // so without these early exits those jobs would wrongly show as appointment statuses.
    if (jobStatus === '10') {
        return DispatchStatus.COMPLETED;
    }
    if (jobStatus === 'survey') {
        return DispatchStatus.SURVEY;
    }
    if (jobStatus === 'sold') {
        return DispatchStatus.SOLD;
    }
    if (jobStatus === '40') {
        return DispatchStatus.CANCELED;
    }
    if (jobStatus === '140') {
        return DispatchStatus.INCOMPLETED;
    }
    if (jobStatus === '150') {
        return DispatchStatus.HOLD;
    }

    if (!jobStatus) {
        return flowStep as DispatchStatus || DispatchStatus.ASSIGNED;
    }

    // Prioritize flow_step for active workflow stages
    if (flowStep === 'appointment') {
        // Map appointment_status to the appropriate DispatchStatus
        const appointmentStatusMap: Record<string, DispatchStatus> = {
            '280': DispatchStatus.LEFT_MESSAGE,
            '60': DispatchStatus.UNABLE_TO_CONTACT,
            '30':  DispatchStatus.APPT_INITIAL_SET,
            '70':  DispatchStatus.APPT_ON_MY_WAY,
            '80':  DispatchStatus.APPT_SET,       // Technician may be delayed
            '90':  DispatchStatus.APPT_ARRIVED,
            '120': DispatchStatus.APPT_MISSED,
            '200': DispatchStatus.APPT_SET,
            '270': DispatchStatus.APPT_SET,       // Reschedule appointment set
            '400': DispatchStatus.APPT_SET,       // Return appointment set
            '430': DispatchStatus.APPT_SET,       // Installation appointment set
            '220': DispatchStatus.APPT_SET,       // Appointment complete
            '230': DispatchStatus.APPT_MISSED,    // Appointment cancelled
        };
        if (appointmentStatus && appointmentStatusMap[appointmentStatus]) {
            return appointmentStatusMap[appointmentStatus];
        }
        // No appointment sub-status — fall back to job_status to decide between Assigned/Accepted
        if (jobStatus === '250') return DispatchStatus.ASSIGNED;
        if (jobStatus === '260') return dispatchUser == null ? DispatchStatus.ACCEPTED : DispatchStatus.ASSIGNED;
        return DispatchStatus.ACCEPTED;
    }

    if (flowStep === 'parts' || flowStep === 'invoice') {
        return DispatchStatus.PROGRESS;
    }

    // Handle special cases with business logic
    switch (jobStatus) {
        case '250': // Assigned
            return DispatchStatus.ASSIGNED;

        case '260': // Accepted - but only if no dispatch user assigned
            return dispatchUser == null ? DispatchStatus.ACCEPTED : DispatchStatus.ASSIGNED;

        case '20': // In Progress - but only if dispatch user is assigned
            return dispatchUser?.user_id ? DispatchStatus.PROGRESS : DispatchStatus.ASSIGNED;

        case '150': // On Hold or Archived
            return flowStep === 'archived' ? DispatchStatus.ARCHIVED : DispatchStatus.HOLD;

        case '10': // Completed
            return DispatchStatus.COMPLETED;

        case 'survey': // Survey (tenant-only, after Complete)
            return DispatchStatus.SURVEY;

        case '40': // Cancelled
            return DispatchStatus.CANCELED;

        case '140': // Incompleted
            return DispatchStatus.INCOMPLETED;

        default:
            // Fallback to flow_step or default mapping
            return JobStatusToDispatchStatus[jobStatus] || (flowStep as DispatchStatus) || DispatchStatus.ASSIGNED;
    }
}

// SignNow Event Type Labels and Colors
export const SignNowEventTypeLabels: Record<string, string> = {
    // Document lifecycle events
    'user.document.create': 'Document Created',
    'user.document.open': 'Document Opened',
    'user.document.update': 'Document Updated',
    'user.document.complete': 'Document Completed',
    'user.document.delete': 'Document Deleted',
    'user.template.copy': 'Template Copied',

    // Field invite events
    'user.document.fieldinvite.create': 'Invite Created',
    'user.document.fieldinvite.sent': 'Invite Sent',
    'user.document.fieldinvite.signed': 'Document Signed',
    'user.document.fieldinvite.reassign': 'Invite Reassigned',
    'user.document.fieldinvite.decline': 'Invite Declined',
    'user.document.fieldinvite.resend': 'Invite Resent',
    'user.document.fieldinvite.replace': 'Invite Replaced',
    'user.document.fieldinvite.delete': 'Invite Deleted',

    // Freeform invite events
    'user.document.freeform.create': 'Freeform Created',
    'user.document.freeform.signed': 'Freeform Signed',
    'user.document.freeform.resend': 'Freeform Resent',
    'user.document.freeform.cancel': 'Freeform Cancelled',

    // Consent events
    'user.document.fieldinvite.consent.agreed': 'Consent Agreed',
    'user.document.fieldinvite.consent.declined': 'Consent Declined',
    'user.document.fieldinvite.consent.withdrawn': 'Consent Withdrawn',

    // Authentication and delivery events
    'user.document.fieldinvite.authentication.failed': 'Authentication Failed',
    'user.document.fieldinvite.email.delivery.failed': 'Email Delivery Failed',

    // Expiration events
    'user.invite.expired': 'Invite Expired',

    // Legacy/simplified events
    'document.signed': 'Document Signed',
    'document.completed': 'Document Completed',
    'document.declined': 'Document Declined',
    'document.cancelled': 'Document Cancelled',
    'document.expired': 'Document Expired',
    'document.viewed': 'Document Viewed',
    'invite.sent': 'Invite Sent',
};

export const SignNowEventTypeColors: Record<string, string> = {
    // Document lifecycle events
    'user.document.create': 'blue',
    'user.document.open': 'cyan',
    'user.document.update': 'geekblue',
    'user.document.complete': 'green',
    'user.document.delete': 'red',
    'user.template.copy': 'purple',

    // Field invite events
    'user.document.fieldinvite.create': 'blue',
    'user.document.fieldinvite.sent': 'cyan',
    'user.document.fieldinvite.signed': 'green',
    'user.document.fieldinvite.reassign': 'orange',
    'user.document.fieldinvite.decline': 'red',
    'user.document.fieldinvite.resend': 'gold',
    'user.document.fieldinvite.replace': 'orange',
    'user.document.fieldinvite.delete': 'red',

    // Freeform invite events
    'user.document.freeform.create': 'blue',
    'user.document.freeform.signed': 'green',
    'user.document.freeform.resend': 'gold',
    'user.document.freeform.cancel': 'red',

    // Consent events
    'user.document.fieldinvite.consent.agreed': 'green',
    'user.document.fieldinvite.consent.declined': 'red',
    'user.document.fieldinvite.consent.withdrawn': 'volcano',

    // Authentication and delivery events
    'user.document.fieldinvite.authentication.failed': 'red',
    'user.document.fieldinvite.email.delivery.failed': 'magenta',

    // Expiration events
    'user.invite.expired': 'orange',

    // Legacy/simplified events
    'document.signed': 'green',
    'document.completed': 'green',
    'document.declined': 'red',
    'document.cancelled': 'red',
    'document.expired': 'orange',
    'document.viewed': 'blue',
    'invite.sent': 'cyan',
};

/**
 * Get nice label for SignNow event type
 */
export function getSignNowEventTypeLabel(eventType: string): string {
    return SignNowEventTypeLabels[eventType] || eventType;
}

/**
 * Get color for SignNow event type
 */
export function getSignNowEventTypeColor(eventType: string): string {
    return SignNowEventTypeColors[eventType] || 'default';
}


